import { Dayjs } from "dayjs";
import { isDefined } from "../../isDefined.ts";

export type TimeInterval = {
  start: Dayjs;
  end: Dayjs;
};

export type HourPiece = {
  hourStart: Dayjs;
  minutes: number;
};

const MINUTES_PER_HOUR = 60;

function minutesOf(interval: TimeInterval): number {
  return Math.max(0, interval.end.diff(interval.start, "minute", true));
}

export function hoursOf(intervals: TimeInterval[]): number {
  const minutes = intervals.reduce(
    (totalMinutes, interval) => totalMinutes + minutesOf(interval),
    0,
  );
  return minutes / MINUTES_PER_HOUR;
}

export function clipToRange(
  interval: TimeInterval,
  range: TimeInterval,
): TimeInterval | null {
  const start = interval.start.isAfter(range.start)
    ? interval.start
    : range.start;
  const end = interval.end.isBefore(range.end) ? interval.end : range.end;
  return start.isBefore(end) ? { start, end } : null;
}

export function clipAllToRange(
  intervals: TimeInterval[],
  range: TimeInterval,
): TimeInterval[] {
  return intervals
    .map((interval) => clipToRange(interval, range))
    .filter(isDefined);
}

export function mergeOverlapping(intervals: TimeInterval[]): TimeInterval[] {
  const sortedByStart = [...intervals].sort(
    (first, second) => first.start.valueOf() - second.start.valueOf(),
  );
  return sortedByStart.reduce<TimeInterval[]>((merged, interval) => {
    const previous = merged[merged.length - 1];
    if (previous && !interval.start.isAfter(previous.end)) {
      if (interval.end.isAfter(previous.end)) previous.end = interval.end;
      return merged;
    }
    merged.push({ ...interval });
    return merged;
  }, []);
}

function cutOut(interval: TimeInterval, cut: TimeInterval): TimeInterval[] {
  if (!clipToRange(interval, cut)) return [interval];
  const remainingPieces: TimeInterval[] = [];
  if (interval.start.isBefore(cut.start))
    remainingPieces.push({ start: interval.start, end: cut.start });
  if (cut.end.isBefore(interval.end))
    remainingPieces.push({ start: cut.end, end: interval.end });
  return remainingPieces;
}

export function subtractIntervals(
  intervals: TimeInterval[],
  intervalsToRemove: TimeInterval[],
): TimeInterval[] {
  return intervalsToRemove.reduce(
    (remaining, intervalToRemove) =>
      remaining.flatMap((interval) => cutOut(interval, intervalToRemove)),
    intervals,
  );
}

export function splitByHour(interval: TimeInterval): HourPiece[] {
  const pieces: HourPiece[] = [];
  for (
    let hourStart = interval.start.startOf("hour");
    hourStart.isBefore(interval.end);
    hourStart = hourStart.add(1, "hour")
  ) {
    const piece = clipToRange(interval, {
      start: hourStart,
      end: hourStart.add(1, "hour"),
    });
    if (piece) pieces.push({ hourStart, minutes: minutesOf(piece) });
  }
  return pieces;
}
